import React, { useState, useEffect } from 'react';
import { Form, CardGroup, Card, CardBody, Row, Col, FormGroup, Label, FormFeedback, InputGroup } from 'reactstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import moment, { ISO_8601 } from 'moment';
import { SingleDatePicker } from 'react-dates';
import { CustomDashboardInput, CustomButton } from '../../containers';
import { yearListRender, FORMAT_STANDARD_DATE } from '../../constants/dateFormatting';
import { validateFormSubmission } from '../../utils/validation';
import { USER_ACCOUNT_TYPES, USER_GENDER } from '../../constants/userDefs';
import { get } from 'lodash-es';
import { PlaceAndZipInput } from '../PlaceAndZipInput';

export const AdminEditPrivateUserProfileData = ({
  user,
  inputHandler,
  toggleAllowEditModal,
  allowEdit,
  places,
  updateProfileInfo,
  loading,
  loadingPlaces,
  countries,
  loadingCountries,
}) => {
  const { formatMessage } = useIntl();
  const [dateInputFocused, setDateInputFocused] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [firstName, setFirstName] = useState({
    value: user.firstName || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.firstName" />,
    validation: {
      required: true,
    },
  });
  const [lastName, setLastName] = useState({
    value: user.lastName || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.lastName" />,
    validation: {
      required: true,
    },
  });
  const [OIB, setOIB] = useState({
    value: user.oib || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.OIB" />,
    validation: {
      required: true,
      maxLength: 11,
      minLength: 11,
      oib: true,
    },
  });
  const [dateOfBirth, setDateOfBirth] = useState({
    value: moment(user.dateOfBirth, ISO_8601, true).isValid() ? moment(user.dateOfBirth) : null,
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.dateOfBirth" />,
    validation: {
      required: true,
      date: {
        valid: true,
      },
    },
  });

  const [sex, setSex] = useState({
    value: user.sex || '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.sex" />,
    validation: {
      required: true,
      enum: USER_GENDER.map(gender => gender.value),
    },
  });

  const [address, setAddress] = useState({
    value: user.address || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.address" />,
    validation: {
      required: true,
    },
  });
  const [city, setCity] = useState({
    value: user.city || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.city" />,
    validation: {
      required: true,
      enum: places.map(place => place.name),
    },
  });
  const [zip, setZip] = useState({
    value: user.zip || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.zipCode" />,
    validation: {
      required: true,
      enum: places.map(place => place.pttNumber),
    },
  });
  const [country, setCountry] = useState({
    value: countries.some(country => country.codeLegacy === user.country) ? user.country : '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.country" />,
    validation: {
      required: true,
      enum: countries.map(country => country.codeLegacy),
    },
  });
  const [sector, setSector] = useState({
    value: user.sector || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.sector" />,
    validation: {
      required: true,
      enum: USER_ACCOUNT_TYPES.map(type => type.name),
    },
  });
  const [passportNumber, setPassportNumber] = useState({
    value: user.passportNumber || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.passportNumber" />,
    validation: {},
  });
  const [governmentIssuedIdNumber, setGovernmentIssuedIdNumber] = useState({
    value: user.governmentIssuedIdNumber || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.governmentIssuedIdNumber" />,
    validation: {},
  });
  const [HZZONumber, setHZZONumber] = useState({
    value: user.HZZONumber || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.HZZONumber" />,
    validation: {
      minLength: 9,
    },
  });
  const [ino, setIno] = useState({
    value: user.ino || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.ino" />,
    validation: {},
  });

  useEffect(() => {
    if (places.length > 0 && city.validation.enum.length < 1) {
      setCity(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.name) } }));
    }
    if (places.length > 0 && zip.validation.enum.length < 1) {
      setZip(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.pttNumber) } }));
    }

    if (countries.length > 0 && country.validation.enum.length < 1) {
      setCountry(o => ({
        ...o,
        value: countries.some(country => country.codeLegacy === user.country) ? user.country : '',
        validation: { ...o.validation, enum: countries.map(country => country.codeLegacy) },
      }));
    }
  }, [places, countries, city, country, user.country, zip]);

  const handleUserProfileDataChange = async event => {
    event.preventDefault();
    setSubmitted(true);
    const fieldsArray = [
      setFirstName,
      setLastName,
      setOIB,
      setDateOfBirth,
      setAddress,
      setCity,
      setCountry,
      setZip,
      setPassportNumber,
      setGovernmentIssuedIdNumber,
      setHZZONumber,
      setIno,
    ];
    !user.sector && fieldsArray.push(setSector);

    if (await validateFormSubmission(fieldsArray)) {
      updateProfileInfo({
        variables: {
          id: user.id,
          email: user.email,
          phoneNumber: user.phoneNumber,
          ...(!user.sector && { sector: sector.value }),
          firstName: firstName.value,
          lastName: lastName.value,
          oib: OIB.value,
          dateOfBirth: dateOfBirth.value,
          sex: sex.value,
          address: address.value,
          city: city.value,
          zip: zip.value,
          country: country.value,
          passportNumber: passportNumber.value,
          governmentIssuedIdNumber: governmentIssuedIdNumber.value,
          HZZONumber: HZZONumber.value,
          ino: ino.value,
        },
      });
    }
  };

  const disableSubmitBtn = () => {
    return (
      !allowEdit ||
      loading ||
      (firstName.value === (get(user, 'firstName', '') || '') &&
        lastName.value === (get(user, 'lastName', '') || '') &&
        OIB.value === (get(user, 'oib', '') || '') &&
        (dateOfBirth.value && dateOfBirth.value.toISOString()) === get(user, 'dateOfBirth', '') &&
        sex.value === (get(user, 'sex', '') || '') &&
        address.value === (get(user, 'address', '') || '') &&
        city.value === (get(user, 'city', '') || '') &&
        zip.value === (get(user, 'zip', '') || '') &&
        governmentIssuedIdNumber.value === (get(user, 'governmentIssuedIdNumber', '') || '') &&
        HZZONumber.value === (get(user, 'HZZONumber', '') || '') &&
        ino.value === (get(user, 'ino', '') || '') &&
        passportNumber.value === (get(user, 'passportNumber', '') || ''))
    );
  };

  return (
    <Form onSubmit={handleUserProfileDataChange}>
      <CardGroup>
        <Card className="hok-card">
          <CardBody>
            <Row className="my-4">
              <Col xs="12">
                <Row className="no-gutters">
                  <Col className="text-right" xs="12">
                    <CustomButton
                      className={allowEdit ? 'hok-dashboard-btn' : 'hok-outline-btn'}
                      outline={!allowEdit}
                      block={false}
                      translationId="changeData"
                      disabled={allowEdit}
                      onClick={event => {
                        event.preventDefault();
                        toggleAllowEditModal();
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="12">
                    <h3>
                      <FormattedMessage id="adminEditUserPage.basicInfo" />
                    </h3>
                  </Col>
                </Row>
                <FormGroup row className="mt-4">
                  <Col sm="6">
                    <Label for="firstName">
                      <FormattedMessage id="inputs.firstName" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="firstName"
                        disabled={!allowEdit}
                        value={firstName.value}
                        fieldState={firstName}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setFirstName, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="lastName">
                      <FormattedMessage id="inputs.lastName" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="lastName"
                        disabled={!allowEdit}
                        value={lastName.value}
                        fieldState={lastName}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setLastName, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="OIB">
                      <FormattedMessage id="inputs.OIB" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="OIB"
                        disabled={!allowEdit}
                        value={OIB.value}
                        fieldState={OIB}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setOIB, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="dateOfBirth">
                      <FormattedMessage id="inputs.dateOfBirth" />
                    </Label>
                    <InputGroup className={dateOfBirth.invalid && submitted ? 'invalid-date' : ''}>
                      <SingleDatePicker
                        id="dateOfBirth"
                        hideKeyboardShortcutsPanel
                        disabled={!allowEdit}
                        date={dateOfBirth.value}
                        onDateChange={date => setDateOfBirth({ ...dateOfBirth, invalid: false, value: date })}
                        showDefaultInputIcon
                        firstDayOfWeek={1}
                        numberOfMonths={1}
                        small
                        focused={dateInputFocused}
                        onFocusChange={({ focused }) => setDateInputFocused(focused)}
                        displayFormat={FORMAT_STANDARD_DATE}
                        placeholder={formatMessage({ id: 'inputs.dateOfBirth' })}
                        isOutsideRange={date => date.isAfter(moment().subtract(18, 'year'), 'day')}
                        initialVisibleMonth={() => dateOfBirth.value || moment().subtract(18, 'year')}
                        renderMonthElement={({ month, onMonthSelect, onYearSelect }) => (
                          <div className="d-flex justify-content-center">
                            <div className="mr-1">
                              <select value={month.month()} onChange={e => onMonthSelect(month, e.target.value)}>
                                {moment.months().map((label, value) => (
                                  <option value={value} key={label}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="ml-1">
                              <select value={month.year()} onChange={e => onYearSelect(month, e.target.value)}>
                                {yearListRender()}
                              </select>
                            </div>
                          </div>
                        )}
                      />
                      {dateOfBirth.invalid && submitted && (
                        <FormFeedback className="d-block">{dateOfBirth.error}</FormFeedback>
                      )}
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="sex">
                      <FormattedMessage id="inputs.sex" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          className="text-uppercase"
                          type="select"
                          id="sex"
                          disabled={!allowEdit}
                          fieldState={sex}
                          onChange={event => inputHandler(setSex, event.target.value)}>
                          <option disabled value="">
                            {formatMessage({ id: 'inputs.sex' })}
                          </option>
                          {USER_GENDER.map(gender => (
                            <option key={gender.value} value={gender.value}>
                              {formatMessage({ id: gender.name })}
                            </option>
                          ))}
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="address">
                      <FormattedMessage id="inputs.address" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="address"
                        disabled={!allowEdit}
                        value={address.value}
                        fieldState={address}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setAddress, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <PlaceAndZipInput
                    places={places}
                    disabled={!allowEdit}
                    formSubmitted={submitted}
                    loadingPlaces={loadingPlaces}
                    zip={zip}
                    setZip={setZip}
                    city={city}
                    setCity={setCity}
                  />
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="country">
                      <FormattedMessage id="inputs.country" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          id="country"
                          type="select"
                          disabled={!!country.value || (!allowEdit && !country.value)}
                          value={country.value}
                          fieldState={country}
                          formSubmitted={submitted}
                          onChange={event => inputHandler(setCountry, event.target.value)}>
                          <option disabled value=""></option>
                          {[{ codeLegacy: 'HR', nameCro: 'HRVATSKA' }].map(country => (
                            <option key={country.codeLegacy} value={country.codeLegacy}>
                              {country.nameCro}
                            </option>
                          ))}
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                  <Col sm="6" className="mb-3">
                    <Label for="sector">
                      <FormattedMessage id="inputs.sector" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          className="text-uppercase"
                          type="select"
                          id="sector"
                          disabled={!allowEdit || user.sector}
                          fieldState={sector}
                          onChange={event => inputHandler(setSector, event.target.value)}>
                          <option disabled value=""></option>
                          {USER_ACCOUNT_TYPES.map(type => (
                            <option key={type.name} value={type.name}>
                              {formatMessage({ id: `adminCreateUserPage.filter.${type.name}` })}
                            </option>
                          ))}
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                </FormGroup>
                <Row className="my-4">
                  <Col xs="12">
                    <h3>
                      <FormattedMessage id="adminEditUserPage.advancedInfo" />
                    </h3>
                  </Col>
                </Row>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="passportNumber">
                      <FormattedMessage id="inputs.passportNumber" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="passportNumber"
                        name="passport number"
                        disabled={!allowEdit}
                        placeholder={true}
                        fieldState={passportNumber}
                        onChange={event => inputHandler(setPassportNumber, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="IdNumber">
                      <FormattedMessage id="inputs.governmentIssuedIdNumber" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="IdNumber"
                        name="government issued id number"
                        disabled={!allowEdit}
                        placeholder={true}
                        placeholderId="governmentIssuedIdNumber"
                        fieldState={governmentIssuedIdNumber}
                        onChange={event => inputHandler(setGovernmentIssuedIdNumber, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="HZZONumber">
                      <FormattedMessage id="inputs.HZZONumber" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="HZZONumber"
                        name="HZZO number"
                        disabled={!allowEdit}
                        placeholder={true}
                        fieldState={HZZONumber}
                        onChange={event => inputHandler(setHZZONumber, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="ino">
                      <FormattedMessage id="inputs.ino" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="ino"
                        disabled={!allowEdit}
                        placeholder={true}
                        fieldState={ino}
                        onChange={event => inputHandler(setIno, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </CardGroup>
      <Row className="no-gutters mt-5">
        <Col className="text-right" xs="12">
          <CustomButton
            className="hok-dashboard-btn"
            block={false}
            translationId="save"
            loaderProp={loading}
            disabled={disableSubmitBtn()}
          />
        </Col>
      </Row>
    </Form>
  );
};
